// Project.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import {
  Avatar,
  GridLayout,
  GridLayoutItem,
} from "@progress/kendo-react-layout";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";
import { FiEdit } from "react-icons/fi";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import {
  activeInactiveProject,
  deleteProject,
  getAllProjects,
} from "./services/project.services";
import { clearProjectID, setProjectID } from "./projectSlice";

const Project: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  return (
    <>
      <DeleteProjectDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Project List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={() => navigate("/project/create")}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              Add New Project
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <ProjectGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const ProjectGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);

  const ProjectList = useAppSelector((state) => state.project.ProjectList);
  const loading = useAppSelector((state) => state.project.loading);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  useEffect(() => {
    dispatch(getAllProjects());
  }, []);

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? ProjectList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const MyEditCommandCell = (props: any) => {
    const handleEdit = (ID: number) => {
      navigate("/project/edit", { state: { ProjectID: ID } });
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteProject"));
      dispatch(setProjectID(ID));
    };
    return (
      <td
        className={`action-td ${props.className}`}
        style={{ ...props.tdProps?.style }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEdit(props.dataItem.ID)}
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const MyActiveInActiveCommandCell = (props: any) => {
    const [IsActive, setIsActive] = useState<boolean>(
      props.dataItem.IsActive ?? false,
    );
    const handleActiveInActive = () => {
      setIsActive(!IsActive);
      dispatch(
        activeInactiveProject({ ID: props.dataItem.ID, IsActive: !IsActive }),
      ).then(() => dispatch(getAllProjects()));
    };
    return (
      <td
        {...props.tdProps}
        style={{ textAlign: "center", ...props.tdProps?.style }}
        className={props.className}
      >
        <Switch
          disabled={!isEditAccess}
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInActive}
          size="small"
        />
      </td>
    );
  };

  const ProjectImageCell = (props: GridCellProps) => (
    <td style={{ ...props.style }} className={props.className}>
      <Avatar
        style={{ width: 50, height: 50, flexBasis: 50 }}
        rounded={"full"}
        type={"image"}
      >
        {props.dataItem?.MainImage ? (
          <img
            src={props.dataItem?.MainImage || "/placeholder-hospital.png"}
            alt={props.dataItem?.HospitalName}
            style={{
              objectFit: "cover",
              width: 50,
              height: 50,
              cursor: "pointer",
            }}
            onClick={() => window.open(props.dataItem?.MainImage, "_blank")}
          />
        ) : (
          <div
            style={{
              fontSize: "14px",
              color: "#fff",
              height: 50,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {props.dataItem?.Title?.charAt(0)?.toUpperCase() || "P"}
          </div>
        )}
      </Avatar>
    </td>
  );

  const DisplayInHomeCell = (props: GridCellProps) => (
    <td style={{ ...props.style }} className={props.className}>
      <span
        style={{
          padding: "3px 8px",
          borderRadius: 4,
          backgroundColor: props.dataItem?.IsDisplayInHome
            ? "#d4edda"
            : "#f8d7da",
          color: props.dataItem?.IsDisplayInHome ? "#155724" : "#721c24",
          fontSize: 12,
          fontWeight: 500,
        }}
      >
        {props.dataItem?.IsDisplayInHome ? "YES" : "NO"}
      </span>
    </td>
  );

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 6 || props.index === 7 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => (
    <td {...props.tdProps} colSpan={1} className={"table-cell"}>
      {props.children}
    </td>
  );

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        style={{ maxHeight: "calc(100vh - 200px)" }}
        filterable={true}
        filter={filter}
        data={
          filter
            ? filterBy(ProjectList, filter).slice(
                page.skip,
                page.take + page.skip,
              )
            : ProjectList.slice(page.skip, page.take + page.skip)
        }
        onFilterChange={handleFilterChange}
        skip={page.skip}
        take={page.take}
        total={ProjectList.length}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 20, 50, 100],
          pageSizeValue: pageSizeValue,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="MainImage"
          title="Image"
          width={"80px"}
          filterable={false}
          cells={{ data: ProjectImageCell }}
        />
        <Column
          field="Title"
          title="Title"
          filterable={true}
          cell={(props: any) => (
            <td>
              {isEditAccess ? (
                <div
                  className="hoverableName"
                  onClick={() =>
                    navigate("/project/edit", {
                      state: { ProjectID: props.dataItem?.ID },
                    })
                  }
                >
                  {props.dataItem?.Title}
                </div>
              ) : (
                <div className="normalName">{props.dataItem?.Title}</div>
              )}
            </td>
          )}
        />
        <Column
          field="PropertyTypeName"
          title="Property Type"
          width={"160px"}
          filterable={true}
        />
        <Column
          field="PropertyStatusName"
          title="Status"
          width={"150px"}
          filterable={true}
        />
        <Column
          field="AreaName"
          title="Area"
          width={"150px"}
          filterable={true}
        />
        {/* <Column
          field="PriceRange"
          title="Price Range"
          width={"150px"}
          filterable={false}
        /> */}
        <Column
          field="IsDisplayInHome"
          title="Home Display"
          width={"130px"}
          filterable={false}
          cells={{ data: DisplayInHomeCell }}
        />
        <Column
          field="IsActive"
          title="Active"
          width={"90px"}
          cells={{ data: MyActiveInActiveCommandCell }}
          filterable={false}
          locked={true}
        />
        {(isEditAccess || isDeleteAccess) && (
          <Column
            field="ID"
            title="Actions"
            width={"110px"}
            cells={{ data: MyEditCommandCell }}
            filterable={false}
            locked={true}
          />
        )}
      </KendoGrid>
      {!ProjectList?.length && !loading && <AlertBox />}
    </>
  );
};

const DeleteProjectDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const ProjectID = useAppSelector((state) => state.project.ProjectID);
  const loading = useAppSelector((state) => state.project.loading);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      await dispatch(deleteProject(ID));
      dispatch(getAllProjects());
      dispatch(closeDialog());
      dispatch(clearProjectID());
    }
  };

  return (
    <>
      {dialogName === "deleteProject" && (
        <AppDialog>
          <>
            <Typography.h5>Delete Project</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this project?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{ display: "flex", justifyContent: "end", gap: "10px" }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(ProjectID)}
                  type="button"
                  disabled={loading}
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default Project;
